import styles from './choose-your-maintenance.module.css'
import { CarMaintenanceStepWrapper } from '../car-maintenance-step-wrapper/car-maintenance-step-wrapper'
import { useMaintenanceContext } from '../../context/maintenance-context'
import {
  Checkbox,
  Text,
  Alert,
  SvgIcon,
  TextLink,
  Textarea,
  InlineRadio,
} from '@cinch-labs/design-system'
import { useEffect, useRef, useState } from 'react'
import { serviceOptionDefaults } from '../../types'
import {
  EventNames,
  TrackingEventTypes,
  useAnalytics,
} from '@cinch-nx/shared-analytics'
import { useServiceOptionsExplainedDrawer } from './service-options-explained'
import { datadogRum } from '@datadog/browser-rum'

const isElectricVehicle = (fuelType?: string): boolean => {
  if (!fuelType) return false
  return fuelType.toLowerCase() === 'electric'
}

const formatPrice = (price: number): string =>
  price.toLocaleString('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

type ServiceType = 'oilAndFilter' | 'standardService' | 'fullService' | null

export const ChooseYourMaintenance: React.FC = () => {
  const {
    vehicleMake,
    vehicleModel,
    vehicleFuelType,
    vrm,
    displayName,
    handleNext,
    dispatch,
    step,
    serviceOptions,
    notes: contextNotes,
    canBookAppointment,
  } = useMaintenanceContext()

  const [mot, setMot] = useState<boolean>(false)
  const [serviceChecked, setServiceChecked] = useState<boolean>(false)
  const [selectedServiceType, setSelectedServiceType] =
    useState<ServiceType>(null)
  const [notes, setNotes] = useState<string>(contextNotes || '')
  const [error, setError] = useState<string>()
  const isCheckboxesSet = useRef(false)
  const servicePageLoadEventSent = useRef(false)
  const { trackEvent } = useAnalytics()

  const { setShowServiceOptionsExplainedDrawer, setIsEv } =
    useServiceOptionsExplainedDrawer()

  const isEV = isElectricVehicle(vehicleFuelType)

  useEffect(() => {
    setIsEv(isEV)
  }, [isEV, setIsEv])

  useEffect(() => {
    if (servicePageLoadEventSent.current === false) {
      const loadChooseServicePage = {
        eventName: `Choose Service Page Load`,
        type: TrackingEventTypes.ADOBE,
        data: {
          event: {
            name: EventNames.load,
            category: 'cinch_servicing',
            action: 'choose_service_page_load',
            label: canBookAppointment ? 'booking_journey' : 'enquiry_journey',
          },
        },
      }

      trackEvent(loadChooseServicePage)
    }

    servicePageLoadEventSent.current = true
  }, [servicePageLoadEventSent])

  const title = 'Choose your service'
  const description = `For your ${vehicleMake} ${vehicleModel} (${vrm})${
    displayName ? ` at ${displayName}` : ''
  }`

  const calculateTotal = (): number => {
    let total = 0
    if (mot) {
      total += serviceOptionDefaults.mot.price
    }
    if (serviceChecked && selectedServiceType) {
      switch (selectedServiceType) {
        case 'oilAndFilter': {
          total += serviceOptionDefaults.oilAndFilter.price
          break
        }
        case 'standardService': {
          total += isEV
            ? serviceOptionDefaults.standardServiceEV.price
            : serviceOptionDefaults.standardService.price
          break
        }
        case 'fullService': {
          total += isEV
            ? serviceOptionDefaults.fullServiceEV.price
            : serviceOptionDefaults.fullService.price
          break
        }
      }
    }
    return total
  }

  const total = calculateTotal()
  const showTotal = mot || (serviceChecked && selectedServiceType !== null)

  const onContinue = () => {
    if (serviceChecked && !selectedServiceType) {
      setError(
        'Please select at least one service option, or remove by unchecking "service"',
      )
      return
    }

    if (mot || (serviceChecked && selectedServiceType)) {
      const serviceOptionsResult = {
        ...serviceOptionDefaults,
        ...(mot
          ? { mot: { ...serviceOptionDefaults.mot, selected: true } }
          : {}),
        ...(serviceChecked && selectedServiceType === 'oilAndFilter' && !isEV
          ? {
              oilAndFilter: {
                ...serviceOptionDefaults.oilAndFilter,
                selected: true,
              },
            }
          : {}),
        ...(serviceChecked && selectedServiceType === 'standardService'
          ? isEV
            ? {
                standardServiceEV: {
                  ...serviceOptionDefaults.standardServiceEV,
                  selected: true,
                },
              }
            : {
                standardService: {
                  ...serviceOptionDefaults.standardService,
                  selected: true,
                },
              }
          : {}),
        ...(serviceChecked && selectedServiceType === 'fullService'
          ? isEV
            ? {
                fullServiceEV: {
                  ...serviceOptionDefaults.fullServiceEV,
                  selected: true,
                },
              }
            : {
                fullService: {
                  ...serviceOptionDefaults.fullService,
                  selected: true,
                },
              }
          : {}),
      }

      const serviceOptionsSelectedNames = Object.values(serviceOptionsResult)
        .reduce((agg, cur) => (cur.selected ? agg + cur.name + ', ' : agg), '')
        .replace(/, $/, '')

      dispatch({
        type: 'SET_SELECTED_SERVICE_OPTIONS',
        serviceOptions: serviceOptionsResult,
        serviceOptionsSelectedNames,
        serviceOptionsSelectedTotalPrice: formatPrice(total),
      })

      dispatch({ type: 'UPDATE_NOTES', notes })

      const chooseMaintenance = {
        eventName: `Service Type Selected`,
        type: TrackingEventTypes.ADOBE,
        data: {
          event: {
            name: EventNames.click,
            category: 'cinch_servicing',
            action: 'service_type_selected',
            label: canBookAppointment ? 'booking_journey' : 'enquiry_journey',
            type: serviceOptionsSelectedNames,
          },
        },
      }

      trackEvent(chooseMaintenance)

      datadogRum.addAction('Cinch Servicing: Service Selected', {
        cinchServicing: true,
        step,
        page: window.location.pathname,
        serviceOptionsSelectedNames,
        notesLength: notes.length,
        hasNotes: notes.length > 0,
        canBookAppointment,
      })

      handleNext()
    } else {
      setError('Please select at least one option')
    }
  }

  if (!isCheckboxesSet.current) {
    setMot(serviceOptions?.mot.selected ?? false)
    const hasServiceSelected =
      serviceOptions?.oilAndFilter.selected ||
      serviceOptions?.standardService.selected ||
      serviceOptions?.fullService.selected ||
      serviceOptions?.standardServiceEV?.selected ||
      serviceOptions?.fullServiceEV?.selected
    setServiceChecked(hasServiceSelected ?? false)

    if (serviceOptions?.oilAndFilter.selected) {
      setSelectedServiceType('oilAndFilter')
    } else if (
      serviceOptions?.standardService.selected ||
      serviceOptions?.standardServiceEV?.selected
    ) {
      setSelectedServiceType('standardService')
    } else if (
      serviceOptions?.fullService.selected ||
      serviceOptions?.fullServiceEV?.selected
    ) {
      setSelectedServiceType('fullService')
    }
    isCheckboxesSet.current = true
  }

  const serviceOptionsExplainedButtonText = 'Service options explained'

  const minServicePrice = isEV
    ? Math.min(
        serviceOptionDefaults.standardServiceEV.price,
        serviceOptionDefaults.fullServiceEV.price,
      )
    : Math.min(
        serviceOptionDefaults.oilAndFilter.price,
        serviceOptionDefaults.standardService.price,
        serviceOptionDefaults.fullService.price,
      )

  const handleServiceTypeChange = (serviceType: ServiceType) => {
    setSelectedServiceType(serviceType)
    setError('')
  }

  const handleServiceOptionsExplainedClick = () => {
    const serviceOptionsExplainedEvent = {
      eventName: `Service options explained drawer opened`,
      type: TrackingEventTypes.ADOBE,
      data: {
        event: {
          name: EventNames.click,
          category: 'cinch_servicing',
          action: 'open',
          label: 'service-options-explained',
          type: 'drawer',
        },
      },
    }
    trackEvent(serviceOptionsExplainedEvent)
    setShowServiceOptionsExplainedDrawer(true)
  }

  return (
    <CarMaintenanceStepWrapper
      title={title}
      description={description}
      onContinue={onContinue}
      totalAmount={formatPrice(total)}
      showTotal={showTotal}
    >
      <div className={styles.optionsContainer}>
        <Checkbox
          label={`MOT for £${serviceOptionDefaults.mot.price.toFixed(2)}`}
          id="mot"
          name="mot"
          checked={mot}
          onChange={() => {
            setMot((mot) => !mot)
            setError('')
          }}
          error={{ show: false, message: 'This needs to be checked' }}
          classNames={{
            wrapper: styles.checkbox,
            label: styles.checkboxLabel,
          }}
          disabled={false}
        />

        <Checkbox
          label={`Service from £${minServicePrice.toFixed(2)}`}
          id="service"
          name="service"
          checked={serviceChecked}
          onChange={() => {
            setServiceChecked((checked) => {
              if (checked) {
                setSelectedServiceType(null)
              }
              return !checked
            })
            setError('')
          }}
          classNames={{
            wrapper: styles.checkbox,
            label: styles.checkboxLabel,
          }}
          disabled={false}
        />

        {serviceChecked && (
          <div className={styles.serviceTypeOptionsContainer}>
            <Text
              fontSizeFixed="s"
              fontWeight="semibold"
              className={styles.serviceTypeQuestion}
            >
              Which service would you like to book?
            </Text>

            {!isEV && (
              <div
                className={`${styles.serviceTypeOption} ${
                  selectedServiceType === 'oilAndFilter'
                    ? styles.serviceTypeOptionSelected
                    : ''
                }`}
                onClick={() => handleServiceTypeChange('oilAndFilter')}
              >
                <InlineRadio
                  name="serviceType"
                  value="oilAndFilter"
                  checked={selectedServiceType === 'oilAndFilter'}
                  onChange={() => handleServiceTypeChange('oilAndFilter')}
                  htmlFor="oilAndFilter"
                  classNames={{ label: styles.radioLabel }}
                >
                  <div className={styles.radioContainer}>
                    <div className={styles.radioCircle}>
                      <div className={styles.radioCircleFilled}></div>
                    </div>
                    <div className={styles.serviceTypeContent}>
                      <div className={styles.serviceTypeHeader}>
                        <Text
                          fontSizeFixed="s"
                          fontWeight="bold"
                          className={`${styles.serviceTypeTitle} ${
                            selectedServiceType === 'oilAndFilter'
                              ? styles.serviceTypeTitleSelected
                              : ''
                          }`}
                        >
                          {serviceOptionDefaults.oilAndFilter.name}
                        </Text>
                        <Text
                          fontSizeFixed="s"
                          fontWeight="bold"
                          className={styles.serviceTypePrice}
                        >
                          £{serviceOptionDefaults.oilAndFilter.price.toFixed(2)}
                        </Text>
                      </div>
                      <Text
                        fontSizeFixed="xs"
                        className={styles.serviceTypeDesc}
                      >
                        (Recommended every 5-7,000 miles)
                      </Text>
                    </div>
                  </div>
                </InlineRadio>
              </div>
            )}

            <div
              className={`${styles.serviceTypeOption} ${
                selectedServiceType === 'standardService'
                  ? styles.serviceTypeOptionSelected
                  : ''
              }`}
              onClick={() => handleServiceTypeChange('standardService')}
            >
              <InlineRadio
                name="serviceType"
                value="standardService"
                checked={selectedServiceType === 'standardService'}
                onChange={() => handleServiceTypeChange('standardService')}
                htmlFor="standardService"
                classNames={{ label: styles.radioLabel }}
              >
                <div className={styles.radioContainer}>
                  <div className={styles.radioCircle}>
                    <div className={styles.radioCircleFilled}></div>
                  </div>
                  <div className={styles.serviceTypeContent}>
                    <div className={styles.serviceTypeHeader}>
                      <Text
                        fontSizeFixed="s"
                        fontWeight="bold"
                        className={`${styles.serviceTypeTitle} ${
                          selectedServiceType === 'standardService'
                            ? styles.serviceTypeTitleSelected
                            : ''
                        }`}
                      >
                        {isEV
                          ? serviceOptionDefaults.standardServiceEV.name
                          : serviceOptionDefaults.standardService.name}
                      </Text>
                      <Text
                        fontSizeFixed="s"
                        fontWeight="bold"
                        className={styles.serviceTypePrice}
                      >
                        £
                        {(isEV
                          ? serviceOptionDefaults.standardServiceEV.price
                          : serviceOptionDefaults.standardService.price
                        ).toFixed(2)}
                      </Text>
                    </div>
                    <Text fontSizeFixed="xs" className={styles.serviceTypeDesc}>
                      (Recommended every 10-12,000 miles)
                    </Text>
                  </div>
                </div>
              </InlineRadio>
            </div>

            <div
              className={`${styles.serviceTypeOption} ${
                selectedServiceType === 'fullService'
                  ? styles.serviceTypeOptionSelected
                  : ''
              }`}
              onClick={() => handleServiceTypeChange('fullService')}
            >
              <InlineRadio
                name="serviceType"
                value="fullService"
                checked={selectedServiceType === 'fullService'}
                onChange={() => handleServiceTypeChange('fullService')}
                htmlFor="fullService"
                classNames={{ label: styles.radioLabel }}
              >
                <div className={styles.radioContainer}>
                  <div className={styles.radioCircle}>
                    <div className={styles.radioCircleFilled}></div>
                  </div>
                  <div className={styles.serviceTypeContent}>
                    <div className={styles.serviceTypeHeader}>
                      <Text
                        fontSizeFixed="s"
                        fontWeight="bold"
                        className={`${styles.serviceTypeTitle} ${
                          selectedServiceType === 'fullService'
                            ? styles.serviceTypeTitleSelected
                            : ''
                        }`}
                      >
                        {isEV
                          ? serviceOptionDefaults.fullServiceEV.name
                          : serviceOptionDefaults.fullService.name}
                      </Text>
                      <Text
                        fontSizeFixed="s"
                        fontWeight="bold"
                        className={styles.serviceTypePrice}
                      >
                        £
                        {(isEV
                          ? serviceOptionDefaults.fullServiceEV.price
                          : serviceOptionDefaults.fullService.price
                        ).toFixed(2)}
                      </Text>
                    </div>
                    <Text fontSizeFixed="xs" className={styles.serviceTypeDesc}>
                      (Recommended every 20-24,000 miles)
                    </Text>
                  </div>
                </div>
              </InlineRadio>
            </div>

            <div className={styles.serviceOptionsLink}>
              <div className={styles.infoIcon}>
                <SvgIcon name="info" color="toreaBay" height={16} />
              </div>
              <TextLink
                element={'a'}
                textProps={{ fontSizeFixed: 's', fontWeight: 'semibold' }}
                onClick={handleServiceOptionsExplainedClick}
              >
                {serviceOptionsExplainedButtonText}
              </TextLink>
            </div>
          </div>
        )}
      </div>

      <div className={styles.bookingNotesContainer}>
        <Text
          element="h3"
          fontSizeFixed="m"
          fontWeight="bold"
          className={styles.bookingNotesTitle}
        >
          Booking notes (optional)
        </Text>
        <Text fontSizeFixed="s" className={styles.bookingNotesDescription}>
          Is there anything specific you'd like us to check?
        </Text>
        <Textarea
          id="booking-notes"
          className={styles.bookingNotesTextarea}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g. warning lights, unusual noise"
          rows={6}
          maxLength={300}
        />
      </div>

      {error && (
        <Alert type="warning" className={styles.validationAlert}>
          {error}
        </Alert>
      )}
    </CarMaintenanceStepWrapper>
  )
}
